import type { JobStatus } from '../types/job'

/** Valid status transitions for operational + review/QC workflow. */
export const JOB_STATUS_TRANSITIONS: Record<string, JobStatus[]> = {
  draft: ['scheduled', 'in_progress', 'cancelled'],
  scheduled: ['in_progress', 'cancelled', 'draft'],
  in_progress: ['completed', 'cancelled', 'scheduled'],
  completed: ['pending_review', 'in_progress'],
  pending_review: ['under_review', 'approved', 'qc_scheduled', 'in_progress'],
  under_review: ['qc_scheduled', 'approved', 'pending_review', 'in_progress'],
  qc_scheduled: ['qc_passed', 'qc_failed', 'under_review'],
  qc_passed: ['approved'],
  qc_failed: ['in_progress', 'pending_review'],
  approved: [],
  cancelled: ['draft', 'scheduled'],
}

export function getAllowedStatusTransitions(current: string): JobStatus[] {
  return JOB_STATUS_TRANSITIONS[current] || []
}

/** Field roles may only move jobs through operational statuses (not review/QC). */
export const FIELD_OPS_STATUSES: JobStatus[] = [
  'draft',
  'scheduled',
  'in_progress',
  'completed',
]

export function filterStatusTransitionsForPermissions(
  transitions: JobStatus[],
  canApproveReview: boolean,
  canCancelJob = false
): JobStatus[] {
  return transitions.filter((s) => {
    if (s === 'cancelled') return canCancelJob
    if (canApproveReview) return true
    return FIELD_OPS_STATUSES.includes(s)
  })
}

export function isReviewWorkflowStatus(status: string): boolean {
  return (
    status !== 'pending_review' &&
    !FIELD_OPS_STATUSES.includes(status as JobStatus)
  )
}

/** Field work finished — job left the active dispatch queue (may still be in office review/QC). */
export const FIELD_COMPLETE_STATUSES: JobStatus[] = [
  'completed',
  'pending_review',
  'under_review',
  'approved',
  'qc_passed',
  'qc_scheduled',
]

export function isFieldCompleteStatus(status: string): boolean {
  return FIELD_COMPLETE_STATUSES.includes(status as JobStatus)
}

/**
 * Statuses shown on the office review queue list.
 * Includes legacy `completed` rows that never transitioned to pending_review.
 */
export const REVIEW_QUEUE_STATUSES: JobStatus[] = [
  'completed',
  'pending_review',
  'under_review',
  'qc_failed',
]

/**
 * Jobs skip the manual "completed" resting state — finishing a job drops it
 * straight into the office review queue. Callers that transition a job to
 * 'completed' should persist this status instead (while still stamping
 * actual_end_time etc. for the 'completed' event).
 * Never returns '' — Postgres job_status enum rejects empty string.
 */
export function resolvePostCompletionStatus(targetStatus: string): JobStatus {
  const next = (targetStatus || '').trim()
  if (!next || next === 'completed') return 'pending_review'
  return next as JobStatus
}

export function getStatusColor(status: string): string {
  switch (status) {
    case 'draft':
      return 'bg-slate-100 text-slate-700'
    case 'scheduled':
      return 'bg-emerald-100 text-emerald-800'
    case 'in_progress':
      return 'bg-sky-100 text-sky-800'
    case 'completed':
      return 'bg-green-100 text-green-800'
    case 'cancelled':
      return 'bg-rose-100 text-rose-800'
    case 'pending_review':
    case 'under_review':
      return 'bg-violet-100 text-violet-800'
    case 'qc_scheduled':
      return 'bg-amber-100 text-amber-800'
    case 'qc_passed':
    case 'approved':
      return 'bg-emerald-100 text-emerald-800'
    case 'qc_failed':
      return 'bg-rose-100 text-rose-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

export function formatStatusLabel(status: string): string {
  if (status === 'pending_review' || status === 'under_review') return 'under review'
  return status.replace(/_/g, ' ')
}
